// hooks/useReportIssue.ts
import { useState } from 'react';
import { AxiosResponse } from 'axios';
import nlpSearchService from '../services/NlpSearchService';

interface ReportIssueData {
  Issue: string;
  SearchQuery?: string;
  ComponentSource: string;
  ErrorContext?: string;
  UserContext?: any;
}

interface UseReportIssueReturn {
  isModalOpen: boolean;
  issue: string;
  feedbackMessage: string | null;
  feedbackType: 'success' | 'error' | null;
  isLoading: boolean;
  openModal: (contextData?: Partial<ReportIssueData>) => void;
  closeModal: () => void;
  setIssue: (issue: string) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
}

export const useReportIssue = (): UseReportIssueReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [issue, setIssue] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [contextData, setContextData] = useState<Partial<ReportIssueData>>({});

 const openModal = (data: Partial<ReportIssueData> = {}) => {
  setContextData(data);

  // ✅ Clear previous state first
  setIssue('');
  setFeedbackMessage(null);
  setFeedbackType(null);

  // ✅ Then set prefilled issue
  setIssue(
    `${data.ComponentSource}\n\nError Details:\n${data.ErrorContext || "No additional context"}`
  );

  setIsModalOpen(true);
};

  const closeModal = () => {
    setIsModalOpen(false);
    setIssue('');
    setFeedbackMessage(null);
    setFeedbackType(null);
    setContextData({});
  };
    const dashboard_Service = new nlpSearchService();


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!issue.trim()) {
      setFeedbackMessage('Please describe the issue');
      setFeedbackType('error');
      return;
    }

    setIsLoading(true);
    
    try {
      const payload = {
        Issue: issue,
        SearchQuery: contextData.SearchQuery || '',
        ComponentSource: contextData.ComponentSource || 'Unknown',
        ErrorContext: contextData.ErrorContext || '',
        UserContext: contextData.UserContext || {},
        Timestamp: new Date().toISOString(),
      };

      const res: AxiosResponse = await dashboard_Service.reportIssue(payload);

      if (res.status >= 200 && res.status < 300) {
        setFeedbackMessage(res.data.Message || 'Issue reported successfully');
        setFeedbackType('success');
        
        // Close modal after successful report
        setTimeout(() => {
          closeModal();
        }, 1500);
      } else {
        setFeedbackMessage('Something went wrong. Please try again.');
        setFeedbackType('error');
      }
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to report issue.';
      setFeedbackMessage(msg);
      setFeedbackType('error');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isModalOpen,
    issue,
    feedbackMessage,
    feedbackType,
    isLoading,
    openModal,
    closeModal,
    setIssue,
    handleSubmit,
  };
};